import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import type { Entity } from '@wix/data-tools-generic-events';
import { ArrowLeft } from 'lucide-react';
import { PageLoader, TextButton } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useLocale } from '../../../../lib/locale-context';
import { useNarrow } from '../../../../lib/use-narrow';
import { useBasePath } from '../../../../lib/use-base-path';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { ACTIVITY_MAP_BI_PAGE, ACTIVITY_MAP_MAESTRO_ENTITY, activityMapAgentEntity } from '../../../../lib/activity-map-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { useMaestroBrief } from '../../../../lib/use-maestro-brief';
import { useAgentName } from '../../../../lib/use-agent-name';
import { useMapWorld } from '../../../../lib/home-map/use-map-world';
import { placeCanvasAgents, placeCanvasChannels, placeCanvasConnectors } from '../../../../lib/home-map/canvas-layout';
import { useMapLive } from '../../../../lib/home-map/use-map-live';
import { useMapHistorySources } from '../../../../lib/home-map/use-map-history-sources';
import { useContactBook, contactIdFromQuery, isRecipientId, type ContactId } from '../../../../lib/contact-book';
import { channelSlot } from '../../../../lib/contact-rows';
import { useTasks } from '../../../../lib/tasks';
import { useMyAgents } from '../../../../lib/my-agents';
import { outreachNodeId } from '../../../../lib/home-map/map-layout';
import { calendarSlug } from '../../../../lib/home-map/channels';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../../../lib/query-client';
import { connectorDescriptionKey, connectorLogoUrl, fetchConnectors } from '../../../../lib/connectors';
import { useProductName } from '../../../../lib/use-brand';
import { useAgentConnections } from '../../../../lib/agent-connections';
import { useRecipient, useWhatsappGroups } from '../../../settings/shared/settings-queries';
import { useMe } from '../../../../lib/me';
import { useOpenPhoneConnect } from '../../../../lib/phone-connect';
import { useSiteInfo } from '../../../../lib/site-info';
import { markChannelWanted, useOutreachPreferences } from '../../../../lib/outreach-preferences';
import { sendFeedback } from '../../../../lib/feedback';
import { WHATSAPP_DIRECT_URL } from '../../settings-whatsapp/constants';
import { connectorsPath } from '../../../../lib/connectors-route';
import { shortRelativeTime, strippedMessageText } from '../../../../lib/contact-rows';
import { threadStage, threadTime, uniqueThreads } from '../../../../lib/outreach-stage';
import { continueOnChannelHref } from '../../../../lib/external-channel';
import { BRIEF_ID, MAESTRO_ID, spaceNodeId, type BucketId, type MapNode } from '../../../../lib/home-map/map-layout';
import type { MapEvent } from '../../../../lib/home-map/map-events';
import { mapSectionBack } from '../../../../lib/home-map/map-section-back';
import { TasksBody } from '../../tasks/TasksBody';
import { GoalsBody } from '../../goals/GoalsBody';
import { RoutinesBody } from '../../routines/RoutinesBody';
import { AgentWorkspaceFilesBody } from '../../files/AgentWorkspaceFilesBody';
import { AppsBody } from '../../apps/AppsBody';
import { ActivityBody } from '../../activity/ActivityBody';
import { OutreachTabBody } from '../../contacts/OutreachTabBody';
import type { MapSelection } from './HomeMap';
import { HomeMap } from './HomeMap';
import type { MapCardContent } from './MapInfoCard';
import { MapTimeline } from './MapTimeline';
import { MapBriefCard } from './MapBriefCard';
import { INLINE_TABS, MapTiles, TILE_ROUTES, type MapTab } from './MapTiles';
import styles from './MapHome.module.css';
import type { BadgeFill } from '../../../../lib/home-map/phone-flow';

/** The most rows a folded "+N" card lists on the canvas; past it, the last row says how many more. */
const FOLDED_ROWS = 6;

/** A card action that is a function rather than a link, reporting itself first (`mapCardBi`) when it has a BI. */
const withBi = (bi: (() => void) | undefined, act: () => void) =>
  bi
    ? () => {
        bi();
        act();
      }
    : act;

/**
 * The activity map, hosted by the home's map view (`Home3Body`), which carries the header and the composer:
 * the live map with its timeline, the entry tiles, and the sections they open.
 *
 * Desktop: the tiles are TABS. Picking one shrinks the map to a band and renders that section beneath it —
 * the SAME `TasksBody` / `GoalsBody` / `RoutinesBody` / files explorer / apps grid / activity log the full
 * pages render — with the tab in the URL (`?tab=files`) so a refresh restores it, and a "Back to the map"
 * above the section. Two states only: LARGE (the map with its controls and the tiles under it) and SMALL
 * (a static band over the open section); any click on the small map brings the large one back. The phone
 * navigates instead (written-down web idiom; the phones push a screen).
 */
export function MapHome({
  canvas = false,
  entryLayer = null,
  zoomSlot = null,
  demoLayer = null,
  popoverLayer = null,
  paused = false,
  demoFill,
  onDemoFill,
}: {
  /**
   * The host draws this map as its full-bleed CANVAS (the Overview home's map view on the desktop): the
   * map fills the layer the host gives it and is drawn edge to edge rather than as a card.
   */
  canvas?: boolean;
  /** The canvas host's screen layer over its dock — where the entry-point lines are drawn (`HomeMap`). */
  entryLayer?: HTMLElement | null;
  /** The canvas host's player band, where the zoom pill goes (`HomeMap`). */
  zoomSlot?: HTMLElement | null;
  /** The canvas host's layer under its dock, where the demo loop draws (`HomeMap`). */
  demoLayer?: HTMLElement | null;
  /** The canvas host's top layer, over its chat bar, where the map's cards open (`HomeMap`). */
  popoverLayer?: HTMLElement | null;
  /** The canvas host's player is paused: the canvas's motion holds still (`HomeMap`). */
  paused?: boolean;
  /** The canvas host's entry badges, which the demo loop fills (`HomeMap`). */
  demoFill?: BadgeFill<string>;
  onDemoFill?: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const router = useRouter();
  const narrow = useNarrow();
  const basePath = useBasePath();
  const agentName = useAgentName();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  // …and no Space districts along its foot.
  const { world: builtWorld, known, drawnAgentIds, agentNames, rosterReady, items: mapItems } = useMapWorld({ withSpaces: !canvas });
  // The canvas carries no Morning Brief and no outreach column: both give way to the ENTRY POINTS docked at
  // its inline end (`Home3EntryPoints`, the brief their first item) — screen furniture outside the map. So the brief's card, its anchor node, the outreach nodes and every wire
  // into them are left off the map. Dropped HERE, not in `buildWorld` — that layout is shared 1:1 with
  // both phones and pinned by their parity tests. A particle bound for one of them then has no node to
  // fly to and is simply not drawn; the live layer prunes it on its own clock.
  const world = useMemo(() => {
    if (!canvas || !builtWorld) return builtWorld;
    const dropped = new Set(builtWorld.nodes.filter((n) => n.id === BRIEF_ID || n.region === 'outreach').map((n) => n.id));
    return {
      // …the channels in move to the canvas frame's own arc, by kind, the tools into its one tight row under
      // the ring, and the agents onto its orbit (`canvas-layout.ts`).
      nodes: placeCanvasAgents(placeCanvasConnectors(placeCanvasChannels(builtWorld.nodes.filter((n) => !dropped.has(n.id))))),
      edges: builtWorld.edges.filter((e) => !dropped.has(e.from) && !dropped.has(e.to)),
    };
  }, [canvas, builtWorld]);
  const sources = useMapHistorySources(known, drawnAgentIds);
  // The threads that NEED the reader, per outreach channel — the count on the channel's tile. A thread
  // needs them when its STAGE says so (an open Decision); both directions, each thread once
  // (`uniqueThreads`), and never a completed one (`threadStage` never reads needsYou without the server).
  const book = useContactBook('');
  const { badges, waiting } = useMemo(() => {
    const badges: Record<string, number> = {};
    // Who is waiting on which channel — what the channel's card lists (the count alone said nothing).
    const waiting: Record<string, { name: string; text: string; href?: string; at: number }[]> = {};
    if (book.kind !== 'ready') return { badges, waiting };
    for (const person of book.contacts) {
      for (const { thread } of uniqueThreads(person)) {
        if (threadStage(thread) !== 'needsYou') continue;
        const slot = channelSlot(thread.channel);
        const slug = slot === 'gmail_connector' ? 'email' : slot === 'app' || slot === 'other' ? null : slot;
        if (!slug) continue;
        badges[outreachNodeId(slug)] = (badges[outreachNodeId(slug)] ?? 0) + 1;
        const personId = person.contact?.personId;
        // A counterparty the book knows only by an address has no person id — link it by its
        // recipient id (with `pkind=recipient`, the same discriminator the tab reads) so the map
        // marker opens its correspondence too, instead of being an unopenable name.
        const openQuery = personId
          ? `person=${encodeURIComponent(personId)}`
          : person.recipientId
            ? `person=${encodeURIComponent(person.recipientId)}&pkind=recipient`
            : undefined;
        (waiting[slug] ??= []).push({
          name: person.contact?.displayName ?? '',
          // The question itself when there is one, else the last thing said (envelope stripped).
          text: thread.needsYou?.question?.trim() || strippedMessageText(thread.lastMessage),
          href: openQuery ? `/?tab=messages&${openQuery}` : undefined,
          at: Date.parse(threadTime(thread) ?? '') || 0,
        });
      }
    }
    for (const list of Object.values(waiting)) list.sort((a, b) => b.at - a.at);
    return { badges, waiting };
  }, [book]);
  const { state: taskState } = useTasks();
  const team = useMyAgents();
  // What the channel and connector cards say: the account's own facts, from the reads the app makes anyway.
  const connectorsQuery = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 });
  const connectorsList = connectorsQuery.data?.connectors ?? [];
  const { connections: coderConnections } = useAgentConnections();
  const recipient = useRecipient().data ?? null;
  const { groups } = useWhatsappGroups();
  const me = useMe();
  const openPhoneConnect = useOpenPhoneConnect();
  const site = useSiteInfo();
  const { prefs } = useOutreachPreferences();
  const [voted, setVoted] = useState<Record<string, boolean>>({});
  // "I want this": file the wish to the feedback channel and mark it on the account — no chat opens.
  const wantChannel = useCallback(
    (slug: string, label: string) => {
      logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'want-channel', answer: slug });
      setVoted((v) => ({ ...v, [slug]: true }));
      void markChannelWanted(slug).catch(() => setVoted((v) => ({ ...v, [slug]: false })));
      void sendFeedback({ feedback: t('connectors.channelsOut.wantSeed', { channel: label }), topic: `Outreach channel wanted: ${label}` }).catch(
        () => undefined,
      );
    },
    [logPageClickBi, t],
  );
  const agentsById = useMemo(() => Object.fromEntries(team.status === 'ready' ? team.team.agents.map((a) => [a.id, a]) : []), [team]);
  const live = useMapLive(known, drawnAgentIds, rosterReady, sources);
  useLoadBi('home-map-replay', { isLoading: live.replay === 'loading', isError: live.replay === 'error' });
  const brief = useMaestroBrief();
  const [bumped, setBumped] = useState<Partial<Record<BucketId, number>>>({});
  // A tick on the timeline: scrub to that moment AND open that event's card.
  const [picked, setPicked] = useState<{ event: MapEvent; seq: number } | null>(null);
  // The phone: the home carries a short live band; tapping it opens the full map over the screen.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!narrow) setExpanded(false);
  }, [narrow]);
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [expanded]);
  const onOpenSpace = useCallback(
    (node: MapNode) => {
      if (!node.href) return;
      logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'map-space', answer: node.id });
      void router.push(node.href, `${basePath}${node.href}`);
    },
    [router, basePath, logPageClickBi],
  );

  // The desktop tab rides the URL. Narrow has no tab state: a tile navigates.
  const tabParam = typeof router.query.tab === 'string' ? router.query.tab : '';
  const tab: MapTab | null = !narrow && (INLINE_TABS as readonly string[]).includes(tabParam) ? (tabParam as MapTab) : null;
  const setTab = useCallback(
    (next: MapTab | null) => {
      const query = { ...router.query };
      if (next) query.tab = next;
      else delete query.tab;
      // A person is a place INSIDE the Outreach tab; leaving that tab leaves the person too.
      if (next !== 'messages') {
        delete query.person;
        delete query.pkind;
      }
      // `as` carries the active mount prefix — the app runs with no basePath (see orion-web CLAUDE.md), so a
      // bare `{ pathname }` replace would drop `/orion-web` and land on the root mount.
      const qs = new URLSearchParams(Object.entries(query).flatMap(([k, v]) => (typeof v === 'string' ? [[k, v]] : []))).toString();
      const as = `${basePath}${router.pathname === '/' ? '/' : router.pathname}${qs ? `?${qs}` : ''}`;
      void router.replace({ pathname: router.pathname, query }, as, { shallow: true, scroll: false });
    },
    [router, basePath],
  );
  const onSelectTile = (bucket: BucketId) => setTab(tab === bucket ? null : bucket);
  // A contact opened inside the Outreach tab rides the URL too (`?tab=messages&person=<id>`, plus
  // `&pkind=recipient` for a counterparty the book knows only by an address, with no person id).
  const personParam = typeof router.query.person === 'string' ? router.query.person : '';
  const contactId: ContactId | null = personParam
    ? contactIdFromQuery(personParam, router.query.pkind)
    : null;
  const setPerson = useCallback(
    (id: ContactId | null) => {
      const query: Record<string, string | string[] | undefined> = { ...router.query, tab: 'messages' };
      if (id) {
        query.person = isRecipientId(id) ? id.recipientId : id.personId;
        if (isRecipientId(id)) query.pkind = 'recipient';
        else delete query.pkind;
      } else {
        delete query.person;
        delete query.pkind;
      }
      const qs = new URLSearchParams(Object.entries(query).flatMap(([k, v]) => (typeof v === 'string' ? [[k, v]] : []))).toString();
      const as = `${basePath}${router.pathname === '/' ? '/' : router.pathname}${qs ? `?${qs}` : ''}`;
      void router.replace({ pathname: router.pathname, query }, as, { shallow: true, scroll: false });
    },
    [router, basePath],
  );
  // A tab set on a wide window must not survive into a narrow one as a hidden state.
  useEffect(() => {
    if (narrow && tabParam) setTab(null);
  }, [narrow, tabParam, setTab]);

  const onLand = useCallback((bucket: BucketId) => setBumped((prev) => ({ ...prev, [bucket]: Date.now() })), []);

  const onTimeline = useCallback(() => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'map-timeline' });
    if (narrow) void router.push('/activity', `${basePath}/activity`);
    else setTab(tab === 'activity' ? null : 'activity');
  }, [narrow, router, basePath, tab, setTab, logPageClickBi]);

  const timeOf = useCallback((at: number) => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(at)), [locale]);
  const onPick = useCallback(
    (event: MapEvent) => {
      logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'timeline-tick', answer: event.kind });
      live.scrub(event.at);
      setPicked((prev) => ({ event, seq: (prev?.seq ?? 0) + 1 }));
    },
    [live.scrub, logPageClickBi],
  );
  const timeline = (
    <MapTimeline
      history={live.history}
      mode={live.mode}
      playhead={live.playhead}
      onPlay={live.play}
      onPause={live.pause}
      onScrub={live.scrub}
      onLive={live.live}
      onPick={onPick}
    />
  );

  // A canvas card's press, reported by its Activity Map name (`native-apps/shared/activity-map-bi/contract.json`):
  // `undefined` off the canvas, so the other maps' cards stay as they were.
  const mapCardBi = useCallback(
    (name: string, opts?: { entity?: Entity; slug?: string; action?: string }): (() => void) | undefined =>
      canvas
        ? () =>
            logPageClickBi(
              { page_name: ACTIVITY_MAP_BI_PAGE, button_name: name, ...(opts?.action ? { action: opts.action } : {}) },
              { ...(opts?.entity ? { entity: opts.entity } : {}), ...(opts?.slug ? { additionalFields: { slug: opts.slug } } : {}) },
            )
        : undefined,
    [canvas, logPageClickBi],
  );
  // What the info card says for each thing on the map — and what its ONE action is.
  const describe = useCallback(
    function describe(sel: MapSelection): MapCardContent | null {
      if (!world) return null;
      const node = (id: string) => world.nodes.find((n) => n.id === id);
      const nameOf = (n?: MapNode) =>
        n
          ? n.id === MAESTRO_ID
            ? agentName.short
            : n.id === BRIEF_ID
            ? t('home.map.brief')
            : n.region === 'buckets'
            ? t(`home.map.bucket.${n.label}`)
            : n.label
          : '';
      const last = (id: string) => live.lastByNode[id];
      // What a node last did, as a sentence: "Wrote a file: log.md" — a bare "log.md" said nothing.
      // The channel is the real one — the string carries it mid-sentence in some languages.
      const recentLine = (event?: MapEvent) => {
        if (!event) return undefined;
        const channel = nameOf(node(event.kind === 'message' ? event.from : event.to));
        return [t(`home.map.event.${event.kind}`, { channel }), event.text].filter(Boolean).join(': ');
      };
      const describeEvent = (event: MapEvent, region: string): MapCardContent => {
        // The card says WHAT HAPPENED in words — "Wrote a file", "Reached out on WhatsApp" — never
        // "Maestro → Personal": the arrow said where a dot flew, not what the agent did.
        // The actor: the agent that logged it, else the main agent — except the meeting, which is everyone's.
        // The actor: the agent that logged it. Unattributed rows name nobody — a reply that CAME IN is the
        // person's, the meeting is everyone's, and a file with no author is not the main agent's by default.
        const who = event.agentId ? agentNames[event.agentId] : undefined;
        const spaceName = event.workspaceId ? node(spaceNodeId(event.workspaceId))?.label : undefined;
        const channel = nameOf(node(event.kind === 'message' ? event.from : event.to));
        const title = t(`home.map.event.${event.kind}`, { channel });
        // Its action opens the THING: the file itself, the task, the goal, the person's thread, the chat.
        const label =
          event.kind === 'file'
            ? t('home.map.openFile')
            : event.kind === 'task' || event.relatedTaskId
            ? t('home.map.openTask')
            : event.kind === 'goal'
            ? t('home.map.openGoal')
            : event.kind === 'app'
            ? t('home.map.openApps')
            : event.kind === 'outreach' || event.kind === 'message'
            ? t('home.map.viewMessage')
            : t('home.map.view');
        const action = event.href
          ? { label, href: event.href }
          : event.relatedTaskId
          ? { label: t('home.map.openTask'), href: `/tasks/${event.relatedTaskId}` }
          : event.kind === 'brief' && brief
          ? { label: t('home.map.readMeeting'), onClick: () => brief.open() }
          : { label: t('home.map.timeline'), onClick: onTimeline };
        return {
          key: event.id,
          region,
          title,
          meta: [who, spaceName, timeOf(event.at)].filter(Boolean).join(' · '),
          text: event.text,
          action,
        };
      };
      if (sel.kind === 'event') return describeEvent(sel.event, t('home.map.eventRegion'));
      if (sel.kind === 'edge') {
        // A wire is the thing at its far end: its card, under the wire's own name.
        const a = node(sel.from);
        const b = node(sel.to);
        if (!a || !b) return null;
        const end = a.id === MAESTRO_ID ? b : a;
        const endCard = describe({ kind: 'node', id: end.id });
        if (!endCard) return null;
        return {
          ...endCard,
          key: `${sel.from}>${sel.to}`,
          region: t('home.map.wire'),
          title: `${nameOf(a)} → ${nameOf(b)}`,
          meta: endCard.meta ?? endCard.title,
        };
      }
      const n = node(sel.id);
      if (!n) return null;
      const recent = last(n.id);
      // A folded "+N" plate names what it hides; its action is the screen that lists them all.
      if (n.folded) {
        const region =
          n.region === 'connectors' ? t('home.map.connectors') : n.region === 'outreach' ? t('home.map.outreach') : t('home.map.channelsIn');
        const href =
          n.region === 'connectors'
            ? connectorsPath({ tab: 'tools' })
            : n.region === 'outreach'
            ? connectorsPath({ tab: 'channels-out' })
            : connectorsPath({ tab: 'channels' });
        // The canvas draws it as a tool's own card (Figma 2032:93721): the "+N" plate, then what it folds as a
        // list — each item's logo and name — and one button. At most `FOLDED_ROWS` rows, the last one then
        // saying how many more, so the card's height holds however many are folded.
        if (canvas) {
          const hidden = (n.hidden ?? []).map((label) => {
            const item = mapItems.find((i) => i.label === label);
            const slug = item?.slug;
            return { name: label, logoUrl: item?.logoUrl ?? (slug && !slug.startsWith('mcp:') ? connectorLogoUrl(slug) : undefined) };
          });
          const shown = hidden.length > FOLDED_ROWS ? hidden.slice(0, FOLDED_ROWS - 1) : hidden;
          return {
            key: n.id,
            region,
            title: t('home.map.moreHidden', { count: n.folded }),
            action: {
              label: t('home.map.manage'),
              href,
              onClick: mapCardBi(n.region === 'connectors' ? 'activity-map-folded:manage:connectors' : 'activity-map-folded:manage:channels'),
            },
            profile: {
              count: n.folded,
              items: shown,
              ...(hidden.length > shown.length ? { more: t('home.map.moreHidden', { count: hidden.length - shown.length }) } : {}),
            },
          };
        }
        return {
          key: n.id,
          region,
          title: t('home.map.moreHidden', { count: n.folded }),
          lines: n.hidden ?? [],
          action: { label: t('home.map.manage'), href },
        };
      }
      switch (n.region) {
        case 'centre':
          // The canvas draws the main agent as the agents' own card (Figma 2032:93672): its mark, its role and
          // what it does — Your Agents' words for it — and the way to its page.
          if (canvas) {
            return {
              key: n.id,
              region: t('home.map.mainAgent'),
              title: agentName.short,
              action: {
                label: t('agents.viewAgent', { name: agentName.short }),
                href: '/agents/maestro',
                onClick: mapCardBi('activity-map-agent-card:view', { entity: ACTIVITY_MAP_MAESTRO_ENTITY }),
              },
              profile: {
                maestro: true,
                role: t('agents.maestro.conductor'),
                description: t('agents.subtitle', { maestroAgentName: agentName.name }),
              },
            };
          }
          return {
            key: n.id,
            region: t('home.map.mainAgent'),
            title: agentName.short,
            meta: recent ? timeOf(recent.at) : undefined,
            text: recentLine(recent),
            action: { label: t('home.map.open'), href: '/agents/maestro' },
          };
        case 'brief':
          return {
            key: n.id,
            region: t('home.map.brief'),
            title: t('home.map.brief'),
            meta: brief ? undefined : t('home.map.quiet'),
            text: brief?.quote?.text ?? brief?.headline,
            action: brief ? { label: t('home.map.readMeeting'), onClick: () => brief.open() } : undefined,
          };
        case 'agents': {
          // The Your Agents card's facts: who they are, what they do, how much is on their desk.
          const id = n.id.slice('agent:'.length);
          const agent = agentsById[id];
          const open =
            taskState.kind === 'ready'
              ? taskState.tasks.filter(
                  (task) => task.agentId === id && task.status !== 'COMPLETED' && task.status !== 'FAILED' && task.status !== 'CANCELLED',
                ).length
              : 0;
          const meta = [agent?.title, open ? t('home.map.openTasks', { count: open }) : undefined, recent ? timeOf(recent.at) : undefined]
            .filter(Boolean)
            .join(' · ');
          // The canvas draws an agent's own card (Figma 2032:93672): who they are, what they do, and the way to
          // their page — not what they did last, which the activity panel already tells.
          if (canvas) {
            return {
              key: n.id,
              region: t('home.map.agent'),
              title: n.label,
              action: {
                label: t('agents.viewAgent', { name: n.label }),
                href: `/agents/${id}`,
                onClick: mapCardBi('activity-map-agent-card:view', { entity: activityMapAgentEntity(id) }),
              },
              profile: { agentId: id, role: agent?.title || undefined, description: agent?.description || undefined },
            };
          }
          return {
            key: n.id,
            region: t('home.map.agent'),
            title: n.label,
            meta: meta || undefined,
            text: recentLine(recent) || agent?.description || undefined,
            action: { label: t('home.map.open'), href: `/agents/${id}` },
            link: open ? { label: t('home.map.openTasks', { count: open }), href: `/tasks?agent=${encodeURIComponent(id)}` } : undefined,
          };
        }
        case 'buckets': {
          const bucket = n.label as BucketId;
          return {
            key: n.id,
            region: t('home.map.bucketRegion'),
            title: t(`home.map.bucket.${bucket}`),
            meta: recent ? timeOf(recent.at) : undefined,
            text: recent?.text,
            action: { label: t('home.map.open'), href: TILE_ROUTES[bucket] },
          };
        }
        case 'connectors':
          // The canvas draws a tool's own card (Figma 2032:93721): its logo and name, what it does, one button.
          if (canvas && n.action === 'connect')
            return {
              key: n.id,
              region: t('home.map.connectors'),
              title: t('home.map.connectMore'),
              action: { label: t('home.map.connect'), href: '/settings/connectors', onClick: mapCardBi('activity-map-connect-more:connect') },
              profile: { plus: true, description: t('home.map.connectMoreText') },
            };
          if (n.action === 'connect')
            return {
              key: n.id,
              region: t('home.map.connectors'),
              title: t('home.map.connectMore'),
              text: t('home.map.connectMoreText'),
              action: { label: t('home.map.connect'), href: '/settings/connectors' },
            };
          {
            // Which ACCOUNT the tool is connected as (the Gmail address, the LinkedIn handle); for Wix, the site.
            // A native calendar row has no toolkit: it is found by the slug the map gave it.
            const status = connectorsList.find((c) => (calendarSlug(c)?.slug ?? c.toolkit) === n.slug);
            const isCalendar = !!status && !!calendarSlug(status);
            const accounts = (status?.accounts ?? []).map((a) => a.label).filter((l): l is string => !!l);
            // Wix: the account's own site(s) — name and address — since the Symphony metasite is siteless.
            const sites = n.slug === 'wix' ? site?.sites ?? [] : [];
            const meta: string =
              n.slug === 'wix'
                ? sites[0]?.name || site?.siteDisplayName || site?.businessName || t('home.map.connected')
                : accounts.length
                ? accounts.join(' · ')
                : recent
                ? timeOf(recent.at)
                : t('home.map.connected');
            const siteUrl = sites[0]?.viewUrl ?? null;
            const manageHref = n.slug === 'wix' || isCalendar ? '/settings/connectors' : connectorsPath({ tab: 'tools', target: n.slug ?? '' });
            if (canvas) {
              // What the tool does: its localized blurb, else the server's, never the key.
              const descKey = status ? connectorDescriptionKey(status) : null;
              const localized = descKey ? t(descKey, { productName }) : '';
              const description = (localized && localized !== descKey ? localized : status?.description) || undefined;
              return {
                key: n.id,
                region: t('home.map.connectors'),
                title: n.label,
                action: {
                  label: t('home.map.manage'),
                  href: manageHref,
                  onClick: mapCardBi('activity-map-chip:connectors', { slug: n.slug, action: 'manage' }),
                },
                // Wix keeps its site, which opens in a new tab.
                link: siteUrl
                  ? { label: t('home.map.viewSite'), href: siteUrl, onClick: mapCardBi('activity-map-chip:view-site', { slug: 'wix' }) }
                  : undefined,
                // …and names it: the site under the title, its address (or the other sites) under the description.
                ...(n.slug === 'wix' && sites.length
                  ? {
                      lines: sites.length > 1 ? sites.slice(1).map((s) => s.name) : siteUrl ? [siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')] : undefined,
                    }
                  : {}),
                profile: {
                  ...(n.slug === 'wix' && sites[0]?.name ? { role: sites[0].name } : {}),
                  description,
                  ...(n.slug === 'wix'
                    ? { wordmark: 'wix' as const }
                    : { logoUrl: n.logoUrl ?? (n.slug && !n.slug.startsWith('mcp:') ? connectorLogoUrl(n.slug) : undefined) }),
                },
              };
            }
            return {
              key: n.id,
              region: t('home.map.connectors'),
              title: n.label,
              meta,
              text: recent?.text,
              lines: sites.length > 1 ? sites.slice(1).map((s) => s.name) : siteUrl ? [siteUrl.replace(/^https?:\/\//, '')] : undefined,
              action: {
                label: t('home.map.manage'),
                href: n.slug === 'wix' || isCalendar ? '/settings/connectors' : connectorsPath({ tab: 'tools', target: n.slug ?? '' }),
              },
              link: siteUrl ? { label: t('home.map.viewSite'), href: siteUrl } : undefined,
            };
          }
        case 'channelsIn': {
          const region = t('home.map.channelsIn');
          const phone = recipient?.phone?.trim() || '';
          // Each of the card's presses on the canvas: `activity-map-chip:channels` with what it does.
          const channelBi = (action: 'manage' | 'connect' | 'send-sms' | 'chat') =>
            mapCardBi('activity-map-chip:channels', { slug: n.slug, action });
          switch (n.slug) {
            case 'whatsapp': {
              // The number Symphony hears you on, and the groups it sits in (`useWhatsappGroups` already returns
              // only the live ones); without a number, connect first.
              const live = groups;
              return {
                key: n.id,
                region,
                title: n.label,
                meta: phone ? phone : t('home.map.notConnected'),
                text: recent?.text,
                lines: live.length ? [t('home.map.groups', { count: live.length }), ...live.slice(0, 4).map((g) => g.subject)] : undefined,
                action: phone
                  ? { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'whatsapp' }), onClick: channelBi('manage') }
                  : { label: t('home.map.connectAndVerify'), onClick: withBi(channelBi('connect'), openPhoneConnect) },
                link: phone ? { label: t('home.map.chatOnWhatsapp'), href: WHATSAPP_DIRECT_URL, onClick: channelBi('chat') } : undefined,
              };
            }
            case 'email-in':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: me?.email || undefined,
                text: recent?.text,
                action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels' }), onClick: channelBi('manage') },
              };
            case 'sms-in':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: phone ? phone : t('home.map.notConnected'),
                text: recent?.text,
                action: phone
                  ? { label: t('home.map.sendSms'), href: continueOnChannelHref('SMS') ?? undefined, onClick: channelBi('send-sms') }
                  : { label: t('home.map.connectAndVerify'), onClick: withBi(channelBi('connect'), openPhoneConnect) },
              };
            case 'web':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: me?.email ? t('home.map.signedInAs', { email: me.email }) : t('home.map.thisBrowser'),
                text: t('home.map.thisBrowser'),
                action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'symphony-apps' }), onClick: channelBi('manage') },
              };
            case 'phone-app':
              return {
                key: n.id,
                region,
                title: n.label,
                meta: t('home.map.installed'),
                text: recent?.text,
                action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'symphony-apps' }), onClick: channelBi('manage') },
              };
            default: {
              if (n.slug?.startsWith('coder:')) {
                const id = n.slug.slice('coder:'.length);
                const conn = coderConnections.find((c) => c.id === id);
                // The client, as the Channels screen describes it: platform, when it connected, when it was last
                // used, and how many threads it holds open.
                const last = conn?.lastUsedDate ? t('home.map.lastUsed', { time: shortRelativeTime(conn.lastUsedDate, undefined, t('contacts.time.now')) }) : undefined;
                const since = conn?.connectedDate ? t('home.map.connectedSince', { time: shortRelativeTime(conn.connectedDate, undefined, t('contacts.time.now')) }) : undefined;
                // The meta line is the freshest fact (platform · last used, else when it connected); the
                // rest are lines, never the same fact twice.
                const meta =
                  [conn?.agentPlatform && conn.agentPlatform !== n.label ? conn.agentPlatform : undefined, last].filter(Boolean).join(' · ') ||
                  since ||
                  t('home.map.connected');
                const lines = [
                  meta === since ? undefined : since,
                  conn?.openThreadCount ? t('home.map.openThreads', { count: conn.openThreadCount }) : undefined,
                  conn?.agentUrl ? conn.agentUrl.replace(/^https?:\/\//, '') : undefined,
                ].filter((l): l is string => !!l);
                return {
                  key: n.id,
                  region,
                  title: n.label,
                  meta,
                  text: recent?.text,
                  lines: lines.length ? lines : undefined,
                  action: { label: t('home.map.manage'), href: connectorsPath({ tab: 'channels', target: 'ai-coders' }), onClick: channelBi('manage') },
                };
              }
              return {
                key: n.id,
                region,
                title: n.label,
                meta: recent ? timeOf(recent.at) : t('home.map.connected'),
                text: recent?.text,
                action: { label: t('home.map.manage'), href: '/settings/connectors', onClick: channelBi('manage') },
              };
            }
          }
        }
        case 'outreach': {
          const connected = n.connected !== false;
          // The threads needing the reader, on this channel: who, and what they ask.
          const due = waiting[n.slug ?? ''] ?? [];
          // One clipped line each: the card is a summary, the thread is the "View message" away.
          const clip = (text: string) => (text.length > 90 ? `${text.slice(0, 89)}…` : text);
          const dueLines = due.slice(0, 3).map((w) => clip([w.name, w.text].filter(Boolean).join(' — ')));
          // Messenger: neither the outreach service nor the Facebook connector can DM a person — say so.
          const messenger = n.slug === 'messenger';
          return {
            key: n.id,
            region: t('home.map.outreach'),
            title: n.label,
            // The Symphony identity that sends on this channel: "{agent} on {channel}".
            meta: connected
              ? due.length
                ? t('home.map.needsYouCount', { count: due.length })
                : t('home.map.sendsAs', { agent: agentName.short, channel: n.label })
              : t('home.map.notConnected'),
            text: messenger && !connected ? t('home.map.messengerUnsupported') : due.length ? undefined : recent?.text,
            lines: dueLines.length ? dueLines : undefined,
            // Both land on the Channels-out section: the sending identity per channel lives there.
            // A live channel: change who sends on it. A coming one has nothing to connect — the action is a
            // VOTE: a Maestro chat that files the wish as feedback (the same channel every wish goes to).
            // A live channel: the last message is the thing to open (its correspondence); changing who sends
            // is the quiet link beside it.
            action: connected
              ? due[0]?.href
                ? { label: t('home.map.viewMessage'), href: due[0].href }
                : recent?.href
                ? { label: t('home.map.viewMessage'), href: recent.href }
                : { label: t('home.map.change'), href: `/settings/connectors/channels-out/${n.slug}` }
              : prefs.wantedChannels.includes(n.slug ?? '') || voted[n.slug ?? '']
              ? { label: t('connectors.channelsOut.wanted'), done: true }
              : { label: t('connectors.channelsOut.want'), onClick: () => wantChannel(n.slug ?? '', n.label) },
            link:
              connected && (due[0]?.href || recent?.href)
                ? { label: t('home.map.change'), href: `/settings/connectors/channels-out/${n.slug}` }
                : undefined,
          };
        }
        default:
          return null;
      }
    },
    [
      world,
      live.lastByNode,
      agentNames,
      agentsById,
      taskState,
      agentName.short,
      brief,
      t,
      timeOf,
      onTimeline,
      connectorsList,
      coderConnections,
      recipient,
      groups,
      me,
      openPhoneConnect,
      site,
      prefs.wantedChannels,
      voted,
      wantChannel,
      waiting,
      mapCardBi,
    ],
  );

  const section = useMemo(() => {
    switch (tab) {
      case 'tasks':
        return <TasksBody filterable />;
      case 'goals':
        return <GoalsBody filterable />;
      case 'routines':
        return <RoutinesBody filterable />;
      case 'files':
        return <AgentWorkspaceFilesBody />;
      case 'apps':
        return <AppsBody />;
      case 'messages':
        return <OutreachTabBody contactId={contactId} onOpen={(id) => setPerson(id)} />;
      case 'activity':
        return <ActivityBody />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `contactId` is derived from these two each render
  }, [tab, personParam, router.query.pkind, setPerson]);

  // Where the section's single back control goes — see `mapSectionBack`.
  const backTarget = mapSectionBack(tab, !!contactId);

  return (
    <div className={`${styles.root}${tab ? ` ${styles.tabbed}` : ''}${canvas ? ` ${styles.canvas}` : ''}`} data-testid="map-home">
      <div className={`${styles.mapWrap}${narrow ? ` ${styles.mapBand}` : ''}`}>
        {world ? (
          <HomeMap
            world={world}
            flights={live.flights}
            settle={live.settle}
            maestroName={agentName.short}
            // The phone's home carries a short live band ("peek"); its expand control opens the full world.
            compact={!!tab || narrow}
            bleed={canvas}
            entryLayer={canvas ? entryLayer : null}
            zoomSlot={canvas ? zoomSlot : null}
            demoLayer={canvas ? demoLayer : null}
            popoverLayer={canvas ? popoverLayer : null}
            paused={canvas && paused}
            demoFill={canvas ? demoFill : undefined}
            onDemoFill={canvas ? onDemoFill : undefined}
            landOn={canvas ? 'world' : 'brief'}
            onGroundClick={() => {
              if (narrow) {
                logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'map-expand' });
                setExpanded(true);
              } else if (tab) setTab(null);
            }}
            onTimeline={onTimeline}
            onLand={onLand}
            onOpenSpace={onOpenSpace}
            describe={describe}
            briefCard={canvas ? undefined : <MapBriefCard />}
            badges={badges}
            picked={picked}
            // No play / pause and no timeline slider on the canvas — the foot keeps only the control bar.
            timeline={canvas ? undefined : timeline}
          />
        ) : (
          <PageLoader />
        )}
      </div>

      {/* The phone's full map: over the whole screen, with its controls and timeline; back returns home. */}
      {narrow &&
        expanded &&
        world &&
        typeof document !== 'undefined' &&
        // Portalled: the home's column is a size CONTAINER, which also contains fixed positioning — an
        // overlay inside it would be pinned to the pane, under the phone's floating hamburger and launcher.
        createPortal(
          <div className={styles.overlay} role="region" aria-label={t('home.map.aria')}>
            <div className={styles.overlayBar}>
              <TextButton size="small" startIcon={<ArrowLeft size={16} aria-hidden className="dir-flip" />} onClick={() => setExpanded(false)}>
                {t('home.map.closeMap')}
              </TextButton>
            </div>
            <div className={styles.overlayMap}>
              <HomeMap
                world={world}
                flights={live.flights}
                settle={live.settle}
                landOn="brief"
                maestroName={agentName.short}
                onTimeline={onTimeline}
                onLand={onLand}
                onOpenSpace={onOpenSpace}
                describe={describe}
                briefCard={<MapBriefCard />}
                badges={badges}
                picked={picked}
                timeline={timeline}
              />
            </div>
          </div>,
          document.body,
        )}

      {/* Not on the canvas: the canvas fills the whole screen, so nothing sits under the map. */}
      {!canvas && <MapTiles selected={tab} onSelect={onSelectTile} bumped={bumped} inline={!narrow} />}

      {section && (
        <section className={styles.section} aria-live="polite">
          {/* ONE back control for the whole open section, and it steps back ONE level: a contact opened
              inside the Outreach tab goes back to that tab's list, everything else goes back to the map.
              No body rendered below may draw its own — the Outreach tab did, and a contact's detail then
              carried two stacked back buttons. */}
          <TextButton
            size="small"
            startIcon={<ArrowLeft size={16} aria-hidden className="dir-flip" />}
            onClick={() => (backTarget === 'outreach' ? setPerson(null) : setTab(null))}
          >
            {backTarget === 'outreach' ? t('home.map.outreach') : t('home.map.backToMap')}
          </TextButton>
          {section}
        </section>
      )}

    </div>
  );
}
